// v3
// Path: frontend/src/components/SyncHub/shared/ModalShell.jsx
// Inputs: isOpen, onClose, title, children, maxWidth
// Outputs: A styled dark-themed modal overlay with z-[100] stacking rendered via React Portal
// Summary: Centralized lightweight modal component with Escape light-dismiss, backdrop click, and dark slate theme.

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

export default function ModalShell({ 
    isOpen, 
    onClose, 
    title, 
    children, 
    maxWidth = 'max-w-2xl' 
}) {
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose?.();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return createPortal(
        <div 
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto animate-in fade-in duration-200"
            role="dialog"
            aria-modal="true"
        >
            <div className={`w-full ${maxWidth} bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden transition-all transform animate-in zoom-in-95 duration-200`}>
                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-slate-800 bg-slate-950/80">
                    <h3 className="text-base font-bold text-white flex items-center gap-2">
                        {title}
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-slate-400 hover:text-white transition-colors p-1 rounded-md hover:bg-slate-800 focus:outline-none"
                    >
                        <span className="material-symbols-outlined text-[20px]">close</span>
                    </button>
                </div>

                {/* Body */}
                <div className="text-slate-200">
                    {children}
                </div>
            </div>
        </div>,
        document.body
    );
}
